import type { KitchenBoardPlate } from "@/lib/api/types";

/**
 * «المطلوب الآن» — THE BATCH STRIP (T13, D170).
 *
 * What the station has to make right now, as dish counts: every QUEUED and
 * PREPARING plate, grouped by the dish's own name plus its version(s) (D59),
 * count first, the biggest batch first. Dish counts only — never per cook,
 * never per bill. Pure; the strip decides where it is drawn.
 */

export type BatchRow = {
  key: string;
  name: string;
  count: number;
};

const COUNTED_STATES = new Set(["QUEUED", "PREPARING"]);

/**
 * The list separator a joined name list reads in: the Arabic comma in
 * Arabic, the Latin one in English (i18n rule — never «، » in English text).
 */
export function listSeparator(locale: string): string {
  return locale === "ar" ? "، " : ", ";
}

function dishName(plate: KitchenBoardPlate, locale: string): string {
  const en = locale === "en";
  const base = en
    ? (plate.baseNameEn ?? plate.nameEn ?? plate.baseNameAr ?? plate.nameAr)
    : (plate.baseNameAr ?? plate.nameAr);
  const variants = (plate.variantNames ?? []).map((variant) =>
    en && variant.nameEn ? variant.nameEn : variant.nameAr,
  );
  return variants.length > 0 ? `${base} — ${variants.join(listSeparator(locale))}` : base;
}

export function batchRows(plates: readonly KitchenBoardPlate[], locale: string): BatchRow[] {
  const rows = new Map<string, BatchRow>();
  for (const plate of plates) {
    if (!COUNTED_STATES.has(plate.fulfillmentState)) continue;
    const key = JSON.stringify([
      plate.baseNameAr ?? plate.nameAr,
      (plate.variantNames ?? []).map((variant) => variant.nameAr),
    ]);
    const row = rows.get(key);
    if (row) row.count += 1;
    else rows.set(key, { key, name: dishName(plate, locale), count: 1 });
  }
  // Biggest batch first; ties keep the order the dishes first appeared in
  // (the board's own first-fire order) — `sort` is stable.
  return [...rows.values()].sort((a, b) => b.count - a.count);
}
